import { useAppSelector } from './store/hooks';
import { useSiteLocale } from './hooks/useLocalizedNavigate';
import { localizePath } from './utils/siteLocale';
import {
    Navigate,
    Outlet,
    Route,
    Routes,
    useLocation,
    useNavigate,
    useParams,
} from 'react-router-dom';
import { SnackbarProvider } from 'notistack';
import { useEffect } from 'react';
import { useAuth } from './hooks/auth/useAuth';
import globalRouter from './utils/globalRouter';
import Header from './components/Header/Header';
import Landing from './components/Landing/Landing';
import Login from './components/Login/Login';
import RequireAuth from './components/RequireAuth/RequireAuth';
import EventPage from './components/EventDetails/EventPage';
import UserPage from './components/UserPage/UserPage';
import CreateEvent from './components/CreateEvent/CreateEvent';
import Register from './components/Register/Register';
import UserSettings from './components/UserSettings/UserSettings';
import EditEvent from './components/EditEvent/EditEvent';
import Footer from './components/Footer/Footer';
import ContactUs from './components/ContactUs/ContactUs';
import Terms from './components/Terms/Terms';
import 'react-datepicker/dist/react-datepicker.css';
import ResetPassword from './components/ResetPassword/ResetPassword';
import Home from './components/Home/Home';
import NextEvents from './components/NextEvents/NextEvents';
import CompleteRegistration from './components/Register/CompleteRegistration';
import Finder from './components/Finder/Finder';

const DEFAULT_SITE_URL =
    process.env.REACT_APP_SITE_URL || 'https://sportwice.com';

function ScrollToTop() {
    const { pathname } = useLocation();

    useEffect(() => {
        document.documentElement.scrollTo({
            top: 0,
            left: 0,
            behavior: 'auto',
        });
    }, [pathname]);

    return null;
}

function GlobalCanonicalFallback() {
    const { pathname } = useLocation();

    useEffect(() => {
        const isLocalizedPublicPage =
            /^\/(en|pl|es)(\/(terms|contact-us))?$/.test(pathname);
        const isRootPath = pathname === '/';

        const shouldSetCanonical = isRootPath || isLocalizedPublicPage;
        const existingCanonical = document.head.querySelector(
            'link[rel="canonical"]'
        ) as HTMLLinkElement | null;

        if (!shouldSetCanonical) {
            existingCanonical?.remove();
            return;
        }

        const normalizedPath =
            pathname === '/' ? '/en' : pathname.replace(/\/+$/, '') || '/en';
        const canonicalUrl = `${DEFAULT_SITE_URL}${normalizedPath}`;

        let canonicalLink = existingCanonical;

        if (!canonicalLink) {
            canonicalLink = document.createElement('link');
            canonicalLink.rel = 'canonical';
            document.head.appendChild(canonicalLink);
        }

        canonicalLink.href = canonicalUrl;
    }, [pathname]);

    return null;
}

export function LocaleRedirect({ path }: { path?: string }) {
    const locale = useSiteLocale();
    const location = useLocation();
    const loading = useAppSelector(state => state.auth.meIsLoading);

    // Wait for the account language before redirecting an unprefixed URL.
    if (loading) return null;

    return (
        <Navigate
            to={`${localizePath(path ?? location.pathname, locale)}${location.search}${location.hash}`}
            state={location.state}
            replace
        />
    );
}

function LanguageRouteGuard() {
    const { lang } = useParams();
    const normalizedLang = lang?.toLowerCase();

    if (normalizedLang !== 'en' && normalizedLang !== 'pl') {
        return <Navigate to="/en" replace />;
    }

    return <Outlet />;
}

function App() {
    useAuth();
    const navigate = useNavigate();
    globalRouter.navigate = navigate;

    return (
        <SnackbarProvider
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
        >
            <div className="App">
                <Header />
                <ScrollToTop />
                <GlobalCanonicalFallback />
                <div className="app-container">
                    <main className="container">
                        <Routes>
                            <Route path="/" element={<LocaleRedirect />} />
                            <Route path="/home" element={<LocaleRedirect />} />
                            <Route path="/events/:eventId/edit" element={<LocaleRedirect />} />
                            <Route path="/users/:userId" element={<LocaleRedirect />} />
                            <Route path="/create" element={<LocaleRedirect />} />
                            <Route path="/my-events" element={<LocaleRedirect />} />
                            <Route path="/settings" element={<LocaleRedirect />} />
                            <Route path="/find" element={<LocaleRedirect />} />
                            <Route
                                path="/finder"
                                element={<LocaleRedirect path="/finder" />}
                            />
                            <Route
                                path="/events/:eventId"
                                element={<LocaleRedirect />}
                            />
                            <Route
                                path="/terms"
                                element={<LocaleRedirect path="/terms" />}
                            />
                            <Route
                                path="/contact-us"
                                element={<LocaleRedirect path="/contact-us" />}
                            />
                            <Route
                                path="/login"
                                element={<LocaleRedirect path="/login" />}
                            />
                            <Route
                                path="/register"
                                element={<LocaleRedirect path="/register" />}
                            />
                            <Route
                                path="/complete-profile"
                                element={
                                    <LocaleRedirect path="/complete-profile" />
                                }
                            />
                            <Route
                                path="/reset-password"
                                element={
                                    <LocaleRedirect path="/reset-password" />
                                }
                            />
                            <Route
                                path="/:lang"
                                element={<LanguageRouteGuard />}
                            >
                                <Route index element={<Landing />} />
                                <Route path="finder" element={<Finder />} />
                                <Route path="find" element={<LocaleRedirect path="/finder" />} />
                                <Route
                                    path="events/:eventId"
                                    element={<EventPage />}
                                />
                                <Route path="login" element={<Login />} />
                                <Route path="register" element={<Register />} />
                                <Route
                                    path="complete-profile"
                                    element={<CompleteRegistration />}
                                />
                                <Route path="terms" element={<Terms />} />
                                <Route
                                    path="reset-password"
                                    element={<ResetPassword />}
                                />
                                <Route
                                    path="contact-us"
                                    element={<ContactUs />}
                                />
                                <Route
                                    path="home"
                                    element={
                                        <RequireAuth>
                                            <Home />
                                        </RequireAuth>
                                    }
                                />
                                <Route
                                    path="events/:eventId/edit"
                                    element={
                                        <RequireAuth>
                                            <EditEvent />
                                        </RequireAuth>
                                    }
                                />
                                <Route
                                    path="users/:userId"
                                    element={
                                        <RequireAuth>
                                            <UserPage />
                                        </RequireAuth>
                                    }
                                />
                                <Route
                                    path="create"
                                    element={
                                        <RequireAuth>
                                            <CreateEvent />
                                        </RequireAuth>
                                    }
                                />
                                <Route
                                    path="my-events"
                                    element={
                                        <RequireAuth>
                                            <NextEvents />
                                        </RequireAuth>
                                    }
                                />
                                <Route
                                    path="settings"
                                    element={
                                        <RequireAuth>
                                            <UserSettings />
                                        </RequireAuth>
                                    }
                                />
                            </Route>
                            {/* Catch-all: redirect to home */}
                            <Route
                                path="*"
                                element={<Navigate to="/" replace />}
                            />
                        </Routes>
                    </main>
                    <Footer />
                </div>
            </div>
        </SnackbarProvider>
    );
}

export default App;
